/**
 * LivretAMLecteur.jsx — Lecteur flipbook du Livret AM Topolino Mission 8H
 *
 * Public (aucune auth) · Accessible via QR code ou lien direct.
 * Features : flipbook + zoom + mode sombre/clair + signets · miniatures ·
 *            mode lecture concentrée · recherche texte plein PDF.
 */

import React, { useEffect, useRef, useState, forwardRef, useCallback, useMemo } from 'react';
import HTMLFlipBook from 'react-pageflip';
import {
  ArrowLeft, ArrowRight, Download, Maximize2, Minimize2, Home,
  ZoomIn, X, Sun, Moon, Bookmark, BookmarkPlus, BookmarkX, BookOpen, LayoutGrid,
  Focus, Search, ChevronLeft, ChevronRight, Loader2,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import useSeo from '../hooks/useSeo';

const BASE = '/livrets/am-8h-2026';
const PDF_URL = `${BASE}/livret-am-topolino-2026.pdf`;
const PAGE_URL = (n) => `${BASE}/pages/p${n}.jpg`;
const THUMB_URL = (n) => `${BASE}/pages/p${n}.jpg`;
const SEARCH_INDEX_URL = `${BASE}/search-index.json`;
const LS_THEME = 'cv-lecteur-theme';
const LS_BOOKMARKS = 'cv-lecteur-bookmarks';
const LS_STRIP = 'cv-lecteur-strip';

// Utilitaire : normalise une chaîne (minuscules + retire accents)
const normalize = (s) =>
  (s || '')
    .toString()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

// eslint-disable-next-line react/display-name
const Page = forwardRef(({ pageNumber, totalPages, theme }, ref) => (
  <div ref={ref} className={`page-wrapper theme-${theme}`} data-testid={`flipbook-page-${pageNumber}`}>
    <img src={PAGE_URL(pageNumber)} alt={`Page ${pageNumber}`} loading="lazy" draggable={false} />
    <div className="page-footer"><span className="page-num">Page {pageNumber} / {totalPages}</span></div>
  </div>
));

export default function LivretAMLecteur() {
  useSeo({
    title: 'Livret AM Topolino — Lecteur en ligne · CodeVoiture.fr',
    description: "Feuillette le livret pédagogique AM (voiturette) Mission 8H directement en ligne. 67 pages · Édition 2026 · Sans compte.",
  });

  const [totalPages, setTotalPages] = useState(67);
  const [currentPage, setCurrentPage] = useState(1);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 420, height: 594 });
  const [zoomOpen, setZoomOpen] = useState(false);
  const [zoomScale, setZoomScale] = useState(1);
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'dark';
    return localStorage.getItem(LS_THEME) || 'dark';
  });
  const [bookmarks, setBookmarks] = useState(() => {
    if (typeof window === 'undefined') return [];
    try { return JSON.parse(localStorage.getItem(LS_BOOKMARKS) || '[]'); } catch { return []; }
  });
  const [showBookmarks, setShowBookmarks] = useState(false);
  const [showStrip, setShowStrip] = useState(() => {
    if (typeof window === 'undefined') return true;
    return localStorage.getItem(LS_STRIP) !== 'false';
  });

  // ── Mode Lecture Concentrée ────────────────────────────────────────────
  const [focusMode, setFocusMode] = useState(false);
  const [focusUIVisible, setFocusUIVisible] = useState(true);
  const focusHideTimerRef = useRef(null);

  // ── Recherche Texte ────────────────────────────────────────────────────
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchIndex, setSearchIndex] = useState(null); // { pages: {n: text} }
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const searchInputRef = useRef(null);

  const bookRef = useRef(null);
  const containerRef = useRef(null);
  const stripRef = useRef(null);

  const isDark = theme === 'dark';
  const isBookmarked = bookmarks.includes(currentPage);

  // Persister thème + signets
  useEffect(() => { try { localStorage.setItem(LS_THEME, theme); } catch {} }, [theme]);
  useEffect(() => { try { localStorage.setItem(LS_BOOKMARKS, JSON.stringify(bookmarks)); } catch {} }, [bookmarks]);
  useEffect(() => { try { localStorage.setItem(LS_STRIP, String(showStrip)); } catch {} }, [showStrip]);

  // Auto-scroll strip pour centrer la page active
  useEffect(() => {
    if (!showStrip || focusMode || !stripRef.current) return;
    const active = stripRef.current.querySelector(`[data-thumb="${currentPage}"]`);
    if (active) {
      active.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [currentPage, showStrip, focusMode]);

  // Nombre de pages depuis l'index local
  useEffect(() => {
    fetch(SEARCH_INDEX_URL)
      .then((r) => r.json())
      .then((d) => { if (d.total_pages > 0) setTotalPages(d.total_pages); })
      .catch(() => {});
  }, []);

  // Adaptation viewport
  useEffect(() => {
    const handleResize = () => {
      const isMobile = window.innerWidth < 768;
      const containerW = window.innerWidth - (isMobile ? 20 : 80);
      const containerH = window.innerHeight - (isMobile ? 140 : 180);
      const RATIO = 210 / 297;
      let width, height;
      if (isMobile) {
        width = Math.min(containerW, 420);
        height = width / RATIO;
        if (height > containerH) { height = containerH; width = height * RATIO; }
      } else {
        width = Math.min(containerW / 2, 450);
        height = width / RATIO;
        if (height > containerH) { height = containerH; width = height * RATIO; }
      }
      setDimensions({ width: Math.floor(width), height: Math.floor(height) });
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleFlip = (e) => setCurrentPage(e.data + 1);
  const goNext = () => {
    if (focusMode) {
      setCurrentPage((p) => Math.min(totalPages, p + 1));
    } else {
      bookRef.current?.pageFlip()?.flipNext();
    }
  };
  const goPrev = () => {
    if (focusMode) {
      setCurrentPage((p) => Math.max(1, p - 1));
    } else {
      bookRef.current?.pageFlip()?.flipPrev();
    }
  };
  const goTo = useCallback((n) => {
    const target = Math.max(1, Math.min(totalPages, n));
    if (focusMode) {
      setCurrentPage(target);
    } else {
      bookRef.current?.pageFlip()?.turnToPage(target - 1);
    }
  }, [focusMode, totalPages]);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement && containerRef.current?.requestFullscreen) {
      containerRef.current.requestFullscreen().then(() => setIsFullscreen(true)).catch(() => {});
    } else if (document.exitFullscreen) {
      document.exitFullscreen().then(() => setIsFullscreen(false)).catch(() => {});
    }
  };

  const toggleBookmark = useCallback(() => {
    setBookmarks((bm) => {
      if (bm.includes(currentPage)) return bm.filter((p) => p !== currentPage);
      return [...bm, currentPage].sort((a, b) => a - b);
    });
  }, [currentPage]);

  // ── Focus Mode : gestion ───────────────────────────────────────────────
  const enterFocusMode = useCallback(() => {
    setFocusMode(true);
    setFocusUIVisible(true);
    // Auto-fullscreen si possible (silencieusement)
    if (!document.fullscreenElement && containerRef.current?.requestFullscreen) {
      containerRef.current.requestFullscreen().catch(() => {});
    }
  }, []);

  const exitFocusMode = useCallback(() => {
    setFocusMode(false);
    setFocusUIVisible(true);
    if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  const scheduleHideFocusUI = useCallback(() => {
    if (focusHideTimerRef.current) clearTimeout(focusHideTimerRef.current);
    focusHideTimerRef.current = setTimeout(() => setFocusUIVisible(false), 2800);
  }, []);

  // Réafficher UI Focus au moindre mouvement
  useEffect(() => {
    if (!focusMode) return;
    const show = () => { setFocusUIVisible(true); scheduleHideFocusUI(); };
    scheduleHideFocusUI();
    window.addEventListener('mousemove', show);
    window.addEventListener('touchstart', show);
    window.addEventListener('keydown', show);
    return () => {
      window.removeEventListener('mousemove', show);
      window.removeEventListener('touchstart', show);
      window.removeEventListener('keydown', show);
      if (focusHideTimerRef.current) clearTimeout(focusHideTimerRef.current);
    };
  }, [focusMode, scheduleHideFocusUI]);

  useEffect(() => {
    const onFsChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
      // Si on sort du plein écran manuellement, quitter le mode Concentré
      if (!document.fullscreenElement && focusMode) {
        setFocusMode(false);
      }
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, [focusMode]);

  // ── Recherche : chargement lazy de l'index ─────────────────────────────
  const loadSearchIndex = useCallback(async () => {
    if (searchIndex || searchLoading) return;
    setSearchLoading(true);
    setSearchError(null);
    try {
      const res = await fetch(SEARCH_INDEX_URL);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setSearchIndex(data);
    } catch (e) {
      setSearchError(e.message || 'Erreur de chargement');
    } finally {
      setSearchLoading(false);
    }
  }, [searchIndex, searchLoading]);

  const openSearch = useCallback(() => {
    setSearchOpen(true);
    loadSearchIndex();
    // Focus l'input après ouverture
    setTimeout(() => searchInputRef.current?.focus(), 50);
  }, [loadSearchIndex]);

  // Résultats de recherche (memoisés)
  const searchResults = useMemo(() => {
    if (!searchIndex?.pages || !searchQuery || searchQuery.trim().length < 3) return [];
    const q = normalize(searchQuery.trim());
    const results = [];
    const pages = searchIndex.pages;
    for (const [pageStr, text] of Object.entries(pages)) {
      const normText = normalize(text);
      const idx = normText.indexOf(q);
      if (idx !== -1) {
        // Extraire snippet original (avec accents/casse d'origine) sur ~ 130 chars centré
        const snippetLen = 140;
        const start = Math.max(0, idx - 55);
        const end = Math.min(text.length, idx + q.length + 85);
        const snippetOrig = text.slice(start, end);
        // Trouver la position du match dans le snippet original (approximation position)
        // On refait la recherche sur le snippet normalisé pour surligner correctement
        const normSnippet = normalize(snippetOrig);
        const matchInSnippet = normSnippet.indexOf(q);
        results.push({
          page: parseInt(pageStr, 10),
          snippet: snippetOrig,
          matchStart: matchInSnippet,
          matchLen: q.length,
          prefix: start > 0 ? '…' : '',
          suffix: end < text.length ? '…' : '',
        });
        if (results.length >= 60) break;
      }
    }
    return results.sort((a, b) => a.page - b.page);
  }, [searchIndex, searchQuery]);

  // ── Raccourcis clavier ─────────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => {
      if (zoomOpen) {
        if (e.key === 'Escape') { setZoomOpen(false); setZoomScale(1); }
        return;
      }
      if (searchOpen) {
        if (e.key === 'Escape') { setSearchOpen(false); }
        return;
      }
      if (focusMode && e.key === 'Escape') { exitFocusMode(); return; }
      // Ctrl/Cmd + F → ouvrir recherche
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        openSearch();
        return;
      }
      if (e.key === 'ArrowRight') goNext();
      else if (e.key === 'ArrowLeft') goPrev();
      else if (e.key === 'Home') goTo(1);
      else if (e.key === 'End') goTo(totalPages);
      else if (e.key === 'b' || e.key === 'B') toggleBookmark();
      else if (e.key === 'z' || e.key === 'Z') setZoomOpen(true);
      else if (e.key === 'f' && !e.ctrlKey && !e.metaKey) {
        // simple "f" → toggle mode concentré
        if (focusMode) exitFocusMode(); else enterFocusMode();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [totalPages, toggleBookmark, zoomOpen, searchOpen, focusMode, openSearch, exitFocusMode, enterFocusMode, goTo]);

  // Couleurs dynamiques selon thème
  const bg = isDark
    ? 'bg-gradient-to-br from-neutral-950 via-red-950/40 to-neutral-950'
    : 'bg-gradient-to-br from-neutral-100 via-red-50 to-neutral-200';
  const headerBg = isDark ? 'bg-black/40 border-white/10' : 'bg-white/70 border-black/10';
  const textPrimary = isDark ? 'text-white' : 'text-neutral-900';
  const textSecondary = isDark ? 'text-white/70' : 'text-neutral-700';
  const btnBase = isDark
    ? 'bg-white/10 hover:bg-white/15 border-white/20 text-white'
    : 'bg-black/5 hover:bg-black/10 border-black/15 text-neutral-900';

  // ═══════════════════════════════════════════════════════════════════════
  //  MODE CONCENTRÉ — Rendu dédié
  // ═══════════════════════════════════════════════════════════════════════
  if (focusMode) {
    return (
      <div
        ref={containerRef}
        className="fixed inset-0 z-[100] bg-neutral-950 overflow-hidden focus-mode-container"
        data-testid="focus-mode-container"
      >
        {/* Fond animé subtil */}
        <div className="absolute inset-0 pointer-events-none focus-bg-gradient" />

        {/* Page centrale */}
        <div className="absolute inset-0 flex items-center justify-center p-4 sm:p-8">
          <img
            key={currentPage}
            src={PAGE_URL(currentPage)}
            alt={`Page ${currentPage}`}
            className="max-h-full max-w-full h-auto w-auto object-contain rounded-lg shadow-2xl focus-page-anim"
            draggable={false}
            data-testid="focus-page-image"
          />
        </div>

        {/* Barre du haut : titre + exit */}
        <div className={`absolute top-0 left-0 right-0 flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 transition-opacity duration-500 ${focusUIVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[9px] font-black uppercase tracking-[0.25em] text-red-400">Mode Concentré</span>
            <span className="hidden sm:block h-4 w-px bg-white/20" />
            <span className="hidden sm:block text-[11px] font-black uppercase tracking-widest text-white/60 truncate" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Mission 8H · Topolino
            </span>
          </div>
          <button
            type="button"
            onClick={exitFocusMode}
            className="inline-flex items-center gap-1.5 h-9 px-3 rounded-full bg-white/10 hover:bg-white/20 border border-white/20 text-white text-[11px] font-black uppercase tracking-widest backdrop-blur-md transition"
            data-testid="btn-focus-exit"
            title="Quitter (Esc)"
          >
            <X className="w-4 h-4" /><span className="hidden sm:inline">Quitter</span>
          </button>
        </div>

        {/* Flèche gauche */}
        <button
          type="button"
          onClick={goPrev}
          disabled={currentPage <= 1}
          className={`absolute left-2 sm:left-6 top-1/2 -translate-y-1/2 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white/10 hover:bg-red-500 hover:border-red-500 border border-white/20 text-white flex items-center justify-center backdrop-blur-md transition-all disabled:opacity-20 disabled:cursor-not-allowed focus-arrow ${focusUIVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          data-testid="btn-focus-prev"
          aria-label="Page précédente"
        >
          <ChevronLeft className="w-6 h-6 sm:w-7 sm:h-7" />
        </button>

        {/* Flèche droite */}
        <button
          type="button"
          onClick={goNext}
          disabled={currentPage >= totalPages}
          className={`absolute right-2 sm:right-6 top-1/2 -translate-y-1/2 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white/10 hover:bg-red-500 hover:border-red-500 border border-white/20 text-white flex items-center justify-center backdrop-blur-md transition-all disabled:opacity-20 disabled:cursor-not-allowed focus-arrow ${focusUIVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          data-testid="btn-focus-next"
          aria-label="Page suivante"
        >
          <ChevronRight className="w-6 h-6 sm:w-7 sm:h-7" />
        </button>

        {/* Barre du bas : indicateur + slider */}
        <div className={`absolute bottom-0 left-0 right-0 px-4 sm:px-8 py-3 sm:py-4 transition-opacity duration-500 ${focusUIVisible ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}>
          <div className="mx-auto max-w-2xl flex items-center gap-3">
            <span className="text-[10px] font-black text-white/70 tracking-widest whitespace-nowrap min-w-[52px]" data-testid="focus-page-indicator">
              {String(currentPage).padStart(2, '0')} / {totalPages}
            </span>
            <input
              type="range"
              min={1}
              max={totalPages}
              value={currentPage}
              onChange={(e) => setCurrentPage(parseInt(e.target.value, 10))}
              className="flex-1 h-1.5 rounded-full appearance-none bg-white/10 accent-red-500 cursor-pointer"
              data-testid="focus-page-slider"
              aria-label="Aller à la page"
            />
            <div className="hidden sm:flex items-center gap-1 text-white/40 text-[9px] font-black uppercase tracking-widest">
              <kbd className="px-1.5 py-0.5 rounded bg-white/10 border border-white/15">←</kbd>
              <kbd className="px-1.5 py-0.5 rounded bg-white/10 border border-white/15">→</kbd>
              <span className="ml-1">naviguer</span>
              <kbd className="ml-2 px-1.5 py-0.5 rounded bg-white/10 border border-white/15">Esc</kbd>
              <span>quitter</span>
            </div>
          </div>
        </div>

        {/* Hint premier lancement */}
        {focusUIVisible && (
          <div className="absolute bottom-16 sm:bottom-20 left-1/2 -translate-x-1/2 pointer-events-none focus-hint">
            <div className="px-3 py-1.5 rounded-full bg-red-500/20 border border-red-500/40 text-red-200 text-[9px] font-black uppercase tracking-[0.25em] backdrop-blur-md">
              ✨ Mode Zen Activé
            </div>
          </div>
        )}

        <style>{`
          .focus-bg-gradient {
            background:
              radial-gradient(1200px 600px at 20% 10%, rgba(220,38,38,0.10), transparent 60%),
              radial-gradient(900px 500px at 90% 90%, rgba(147,51,234,0.08), transparent 55%),
              radial-gradient(600px 400px at 50% 50%, rgba(255,255,255,0.02), transparent 70%);
            animation: focus-bg-pulse 12s ease-in-out infinite;
          }
          @keyframes focus-bg-pulse {
            0%, 100% { opacity: 1; transform: scale(1); }
            50% { opacity: 0.75; transform: scale(1.03); }
          }
          .focus-page-anim {
            animation: focus-page-in 0.45s cubic-bezier(0.22, 1, 0.36, 1);
          }
          @keyframes focus-page-in {
            from { opacity: 0; transform: translateY(12px) scale(0.98); }
            to { opacity: 1; transform: translateY(0) scale(1); }
          }
          .focus-arrow:hover { transform: translateY(-50%) scale(1.08); }
          .focus-arrow { transition: transform 0.2s, background 0.2s, border-color 0.2s, opacity 0.5s; }
          .focus-hint {
            animation: focus-hint-in 0.6s ease-out;
          }
          @keyframes focus-hint-in {
            from { opacity: 0; transform: translate(-50%, 10px); }
            to { opacity: 1; transform: translate(-50%, 0); }
          }
        `}</style>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════
  //  RENDU STANDARD (flipbook)
  // ═══════════════════════════════════════════════════════════════════════
  return (
    <div ref={containerRef} className={`min-h-screen ${bg} flex flex-col transition-colors`} data-testid="livret-am-lecteur">
      {/* Header */}
      <header className={`flex items-center justify-between gap-2 px-3 py-2 sm:px-6 sm:py-3 border-b backdrop-blur-md ${headerBg} flex-wrap`}>
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <Link to="/" className={`inline-flex items-center gap-1.5 ${textSecondary} hover:${textPrimary} transition text-[11px] font-black uppercase tracking-widest`} data-testid="btn-back-home">
            <Home className="w-4 h-4" /> <span className="hidden sm:inline">Accueil</span>
          </Link>
          <div className={`h-5 w-px ${isDark ? 'bg-white/20' : 'bg-black/20'}`} />
          <div className="min-w-0">
            <div className="text-[9px] font-black uppercase tracking-[0.22em] text-red-500">Livret AM · Lecteur</div>
            <h1 className={`text-[13px] sm:text-base font-black ${textPrimary} leading-tight truncate`} style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Mission 8H · Édition 2026
            </h1>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          {/* Recherche */}
          <button type="button" onClick={openSearch} className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border transition ${btnBase}`} data-testid="btn-search-open" title="Rechercher (Ctrl+F)" aria-label="Rechercher dans le livret">
            <Search className="w-4 h-4" />
          </button>

          {/* Mode Concentré */}
          <button type="button" onClick={enterFocusMode} className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border transition ${btnBase} hover:!bg-red-500 hover:!border-red-500 hover:!text-white`} data-testid="btn-focus-mode" title="Mode lecture concentrée (F)" aria-label="Mode lecture concentrée">
            <Focus className="w-4 h-4" />
          </button>

          {/* Zoom */}
          <button type="button" onClick={() => setZoomOpen(true)} className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border transition ${btnBase}`} data-testid="btn-zoom-open" title="Zoom (Z)" aria-label="Zoom">
            <ZoomIn className="w-4 h-4" />
          </button>

          {/* Signet */}
          <button type="button" onClick={toggleBookmark} className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border transition ${isBookmarked ? 'bg-yellow-400 border-yellow-400 text-black hover:bg-yellow-300' : btnBase}`} data-testid="btn-bookmark-toggle" title={isBookmarked ? 'Retirer signet (B)' : 'Ajouter signet (B)'} aria-label="Signet">
            {isBookmarked ? <Bookmark className="w-4 h-4" fill="currentColor" /> : <BookmarkPlus className="w-4 h-4" />}
          </button>

          {/* Liste signets */}
          <div className="relative">
            <button type="button" onClick={() => setShowBookmarks((s) => !s)} className={`inline-flex items-center gap-1 h-9 px-2.5 rounded-lg border transition ${btnBase}`} data-testid="btn-bookmarks-list" title="Mes signets" aria-label="Mes signets">
              <BookOpen className="w-4 h-4" />
              <span className="text-[11px] font-black">{bookmarks.length}</span>
            </button>
            {showBookmarks && (
              <div className={`absolute right-0 top-11 z-30 min-w-[180px] max-h-[260px] overflow-auto rounded-xl border shadow-2xl backdrop-blur-lg ${isDark ? 'bg-neutral-900/95 border-white/15' : 'bg-white/95 border-black/10'}`} data-testid="bookmarks-panel">
                <div className={`px-3 py-2 text-[10px] font-black uppercase tracking-widest ${textSecondary} border-b ${isDark ? 'border-white/10' : 'border-black/10'}`}>
                  Mes signets
                </div>
                {bookmarks.length === 0 ? (
                  <div className={`px-3 py-4 text-[12px] ${textSecondary}`}>Aucun signet. Appuie sur ⭐ ou B pour marquer la page en cours.</div>
                ) : (
                  <ul>
                    {bookmarks.map((p) => (
                      <li key={p} className={`flex items-center justify-between px-3 py-2 hover:${isDark ? 'bg-white/10' : 'bg-black/5'} transition group cursor-pointer`}>
                        <button type="button" onClick={() => { goTo(p); setShowBookmarks(false); }} className={`flex-1 text-left text-[13px] font-black ${textPrimary}`} data-testid={`bookmark-goto-${p}`}>
                          Page {p}
                        </button>
                        <button type="button" onClick={() => setBookmarks((bm) => bm.filter((x) => x !== p))} className={`p-1 rounded ${isDark ? 'hover:bg-red-500/30' : 'hover:bg-red-500/20'} text-red-500 opacity-60 group-hover:opacity-100`} data-testid={`bookmark-remove-${p}`} aria-label={`Supprimer signet page ${p}`}>
                          <BookmarkX className="w-3.5 h-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          {/* Thème */}
          <button type="button" onClick={() => setTheme(isDark ? 'light' : 'dark')} className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border transition ${btnBase}`} data-testid="btn-theme-toggle" title={isDark ? 'Mode clair' : 'Mode sombre'} aria-label="Basculer thème">
            {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>

          {/* Toggle strip miniatures */}
          <button type="button" onClick={() => setShowStrip((s) => !s)} className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border transition ${showStrip ? 'bg-red-500 border-red-500 text-white hover:bg-red-400' : btnBase}`} data-testid="btn-toggle-strip" title={showStrip ? 'Masquer miniatures' : 'Afficher miniatures'} aria-label="Basculer bande de miniatures">
            <LayoutGrid className="w-4 h-4" />
          </button>

          {/* PDF */}
          <a href={PDF_URL} download="Livret-AM-Topolino-Mission-8H-2026.pdf" target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-1.5 h-9 px-2.5 rounded-lg border transition ${btnBase}`} data-testid="btn-download-pdf-lecteur" title="Télécharger le PDF">
            <Download className="w-3.5 h-3.5" /><span className="hidden sm:inline text-[11px] font-black uppercase tracking-widest">PDF</span>
          </a>

          {/* Fullscreen */}
          <button type="button" onClick={toggleFullscreen} className={`inline-flex items-center justify-center w-9 h-9 rounded-lg border transition ${btnBase}`} data-testid="btn-toggle-fullscreen" title={isFullscreen ? 'Quitter plein écran' : 'Plein écran'} aria-label="Plein écran">
            {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
          </button>
        </div>
      </header>

      {/* Zone du livre */}
      <div className="flex-1 flex flex-col items-center justify-center p-3 sm:p-6 overflow-hidden">
        <div className="flipbook-shell" data-testid="flipbook-container">
          <HTMLFlipBook
            ref={bookRef}
            width={dimensions.width}
            height={dimensions.height}
            size="stretch"
            minWidth={280}
            maxWidth={700}
            minHeight={396}
            maxHeight={990}
            drawShadow
            flippingTime={700}
            usePortrait
            startZIndex={0}
            autoSize
            maxShadowOpacity={0.5}
            showCover
            mobileScrollSupport={false}
            clickEventForward
            useMouseEvents
            swipeDistance={30}
            showPageCorners
            disableFlipByClick={false}
            onFlip={handleFlip}
            className="flipbook"
            style={{}}
          >
            {Array.from({ length: totalPages }, (_, i) => (
              <Page key={i + 1} pageNumber={i + 1} totalPages={totalPages} theme={theme} />
            ))}
          </HTMLFlipBook>
        </div>
      </div>

      {/* Contrôles bas */}
      <footer className={`px-3 py-2.5 sm:px-6 sm:py-3.5 border-t backdrop-blur-md ${headerBg}`}>
        <div className="flex items-center justify-between gap-3 max-w-4xl mx-auto">
          <button type="button" onClick={goPrev} disabled={currentPage <= 1} className="inline-flex items-center gap-1.5 h-10 px-3 sm:px-4 rounded-lg bg-red-500 hover:bg-red-400 disabled:opacity-30 disabled:cursor-not-allowed text-white font-black uppercase tracking-widest text-[11px] transition" data-testid="btn-prev-page">
            <ArrowLeft className="w-4 h-4" /><span className="hidden sm:inline">Précédent</span>
          </button>

          <div className="flex-1 flex items-center justify-center gap-2 min-w-0">
            <BookOpen className="w-4 h-4 text-red-500 flex-shrink-0" />
            <input type="range" min={1} max={totalPages} value={currentPage} onChange={(e) => { const p = parseInt(e.target.value, 10); setCurrentPage(p); goTo(p); }} className="flex-1 h-1.5 rounded-full appearance-none bg-red-500/20 accent-red-500 cursor-pointer max-w-[280px]" data-testid="page-slider" aria-label="Aller à la page" />
            <span className={`text-[11px] font-black ${textPrimary} tracking-widest whitespace-nowrap min-w-[52px] text-right`} data-testid="current-page">{currentPage} / {totalPages}</span>
          </div>

          <button type="button" onClick={goNext} disabled={currentPage >= totalPages} className="inline-flex items-center gap-1.5 h-10 px-3 sm:px-4 rounded-lg bg-red-500 hover:bg-red-400 disabled:opacity-30 disabled:cursor-not-allowed text-white font-black uppercase tracking-widest text-[11px] transition" data-testid="btn-next-page">
            <span className="hidden sm:inline">Suivant</span><ArrowRight className="w-4 h-4" />
          </button>
        </div>
        <p className={`text-[10px] ${textSecondary} text-center mt-1.5 hidden sm:block`}>
          Raccourcis : ← → tourner · <kbd className={`px-1 rounded ${isDark ? 'bg-white/10' : 'bg-black/10'}`}>Z</kbd> zoom · <kbd className={`px-1 rounded ${isDark ? 'bg-white/10' : 'bg-black/10'}`}>B</kbd> signet · <kbd className={`px-1 rounded ${isDark ? 'bg-white/10' : 'bg-black/10'}`}>F</kbd> concentré · <kbd className={`px-1 rounded ${isDark ? 'bg-white/10' : 'bg-black/10'}`}>Ctrl+F</kbd> chercher
        </p>
      </footer>

      {/* Bande de miniatures */}
      {showStrip && (
        <div className={`border-t backdrop-blur-md ${headerBg}`} data-testid="strip-thumbnails">
          <div ref={stripRef} className="flex gap-1.5 sm:gap-2 overflow-x-auto py-2 px-3 sm:px-6 scroll-smooth" style={{ scrollbarWidth: 'thin' }}>
            {Array.from({ length: totalPages }, (_, i) => {
              const n = i + 1;
              const active = currentPage === n;
              const bookmarked = bookmarks.includes(n);
              return (
                <button
                  key={n}
                  type="button"
                  data-thumb={n}
                  data-testid={`strip-thumb-${n}`}
                  onClick={() => goTo(n)}
                  className={`flex-shrink-0 relative rounded-md overflow-hidden border-2 transition-all ${
                    active
                      ? 'border-red-500 scale-105 shadow-lg shadow-red-500/40'
                      : `${isDark ? 'border-white/15 hover:border-white/40' : 'border-black/15 hover:border-black/40'} opacity-70 hover:opacity-100`
                  }`}
                  style={{ width: 48, height: 68 }}
                  title={`Page ${n}${bookmarked ? ' · signet' : ''}`}
                  aria-label={`Aller à la page ${n}`}
                >
                  <img src={THUMB_URL(n)} alt={`Miniature page ${n}`} loading="lazy" draggable={false}
                    className="w-full h-full object-cover pointer-events-none" />
                  {bookmarked && (
                    <span className="absolute top-0 right-0 w-3 h-3 bg-yellow-400 rounded-bl-md flex items-center justify-center">
                      <Bookmark className="w-2 h-2 text-black" fill="currentColor" />
                    </span>
                  )}
                  <span className={`absolute bottom-0 left-0 right-0 text-[8px] font-black text-center py-0.5 ${
                    active ? 'bg-red-500 text-white' : 'bg-black/70 text-white/90'
                  }`}>
                    {n}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Modal Zoom */}
      {zoomOpen && (
        <div className="fixed inset-0 z-50 flex flex-col bg-black/95 backdrop-blur" data-testid="zoom-modal" onClick={(e) => { if (e.target === e.currentTarget) { setZoomOpen(false); setZoomScale(1); } }}>
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="text-[11px] font-black uppercase tracking-widest text-white/85">
              🔍 Zoom · Page {currentPage} / {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <button type="button" onClick={() => setZoomScale((z) => Math.max(0.5, z - 0.25))} className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/15 border border-white/20 text-white font-black text-lg" data-testid="btn-zoom-out">−</button>
              <span className="text-white/80 text-[12px] font-black min-w-[44px] text-center">{Math.round(zoomScale * 100)}%</span>
              <button type="button" onClick={() => setZoomScale((z) => Math.min(4, z + 0.25))} className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/15 border border-white/20 text-white font-black text-lg" data-testid="btn-zoom-in">+</button>
              <button type="button" onClick={() => setZoomScale(1)} className="h-9 px-3 rounded-lg bg-white/10 hover:bg-white/15 border border-white/20 text-white text-[11px] font-black uppercase tracking-widest" data-testid="btn-zoom-reset">100%</button>
              <button type="button" onClick={() => { setZoomOpen(false); setZoomScale(1); }} className="w-9 h-9 rounded-lg bg-red-500 hover:bg-red-400 text-white flex items-center justify-center" data-testid="btn-zoom-close" aria-label="Fermer zoom">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="flex-1 overflow-auto flex items-start justify-center p-4">
            <div style={{ transform: `scale(${zoomScale})`, transformOrigin: 'top center', transition: 'transform 0.2s ease-out' }}>
              <img src={PAGE_URL(currentPage)} alt={`Page ${currentPage} zoomée`} className="max-w-full h-auto rounded-md shadow-2xl" draggable={false} data-testid="zoom-image" />
            </div>
          </div>
          <div className="px-4 py-2 border-t border-white/10 text-center text-[10px] text-white/50">
            Pince pour zoomer sur mobile · Clic hors zone ou <kbd className="px-1 rounded bg-white/10">Échap</kbd> pour fermer
          </div>
        </div>
      )}

      {/* Modal Recherche */}
      {searchOpen && (
        <div
          className="fixed inset-0 z-[60] flex items-start justify-center bg-black/70 backdrop-blur-sm p-3 sm:p-6 pt-12 sm:pt-20"
          data-testid="search-modal"
          onClick={(e) => { if (e.target === e.currentTarget) setSearchOpen(false); }}
        >
          <div className={`w-full max-w-2xl rounded-2xl border shadow-2xl overflow-hidden flex flex-col ${isDark ? 'bg-neutral-900 border-white/15' : 'bg-white border-black/10'}`} style={{ maxHeight: 'calc(100vh - 100px)' }}>
            {/* Header modal */}
            <div className={`flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-3 border-b ${isDark ? 'border-white/10' : 'border-black/10'}`}>
              <Search className="w-4 h-4 text-red-500 flex-shrink-0" />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Rechercher un mot dans le livret..."
                className={`flex-1 bg-transparent outline-none text-[14px] sm:text-[15px] font-medium ${textPrimary} placeholder:${isDark ? 'text-white/40' : 'text-black/40'}`}
                data-testid="search-input"
                autoComplete="off"
                spellCheck={false}
              />
              {searchQuery && (
                <button type="button" onClick={() => setSearchQuery('')} className={`w-7 h-7 rounded-full flex items-center justify-center ${isDark ? 'hover:bg-white/10' : 'hover:bg-black/10'} ${textSecondary}`} data-testid="btn-search-clear" aria-label="Effacer">
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
              <button type="button" onClick={() => setSearchOpen(false)} className={`w-8 h-8 rounded-lg flex items-center justify-center ${isDark ? 'bg-white/5 hover:bg-white/10' : 'bg-black/5 hover:bg-black/10'} ${textPrimary}`} data-testid="btn-search-close" aria-label="Fermer recherche">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Corps modal */}
            <div className="flex-1 overflow-y-auto">
              {searchLoading && (
                <div className="flex items-center justify-center gap-2 py-10">
                  <Loader2 className="w-4 h-4 animate-spin text-red-500" />
                  <span className={`text-[12px] ${textSecondary}`}>Chargement de l'index...</span>
                </div>
              )}
              {searchError && !searchLoading && (
                <div className="p-6 text-center">
                  <div className={`text-[12px] text-red-500 font-black mb-2`}>Erreur de chargement</div>
                  <div className={`text-[11px] ${textSecondary} mb-3`}>{searchError}</div>
                  <button type="button" onClick={loadSearchIndex} className="h-9 px-4 rounded-lg bg-red-500 hover:bg-red-400 text-white text-[11px] font-black uppercase tracking-widest" data-testid="btn-search-retry">
                    Réessayer
                  </button>
                </div>
              )}
              {!searchLoading && !searchError && searchQuery.trim().length < 3 && (
                <div className={`px-5 py-8 text-center ${textSecondary}`}>
                  <Search className="w-8 h-8 mx-auto mb-3 opacity-30" />
                  <div className="text-[12px] font-black uppercase tracking-widest mb-1">Recherche plein texte</div>
                  <div className="text-[11px] opacity-70">Tape au moins 3 caractères pour rechercher</div>
                  <div className="text-[10px] opacity-50 mt-3">Insensible aux accents et à la casse · 67 pages indexées</div>
                </div>
              )}
              {!searchLoading && !searchError && searchQuery.trim().length >= 3 && searchResults.length === 0 && (
                <div className={`px-5 py-10 text-center ${textSecondary}`}>
                  <div className="text-3xl mb-2 opacity-30">🔍</div>
                  <div className="text-[13px] font-black">Aucun résultat</div>
                  <div className="text-[11px] opacity-70 mt-1">« {searchQuery} » n'apparaît nulle part dans le livret</div>
                </div>
              )}
              {!searchLoading && searchResults.length > 0 && (
                <>
                  <div className={`px-4 sm:px-5 py-2 text-[10px] font-black uppercase tracking-widest ${textSecondary} border-b ${isDark ? 'border-white/10 bg-white/5' : 'border-black/10 bg-black/5'}`}>
                    {searchResults.length} résultat{searchResults.length > 1 ? 's' : ''} · {new Set(searchResults.map((r) => r.page)).size} page{new Set(searchResults.map((r) => r.page)).size > 1 ? 's' : ''}
                  </div>
                  <ul className="divide-y divide-white/5">
                    {searchResults.map((r, i) => {
                      const before = r.matchStart >= 0 ? r.snippet.slice(0, r.matchStart) : r.snippet;
                      const match = r.matchStart >= 0 ? r.snippet.slice(r.matchStart, r.matchStart + r.matchLen) : '';
                      const after = r.matchStart >= 0 ? r.snippet.slice(r.matchStart + r.matchLen) : '';
                      return (
                        <li key={`${r.page}-${i}`}>
                          <button
                            type="button"
                            onClick={() => { goTo(r.page); setSearchOpen(false); }}
                            className={`w-full text-left px-4 sm:px-5 py-3 flex items-start gap-3 group transition ${isDark ? 'hover:bg-white/5' : 'hover:bg-black/5'}`}
                            data-testid={`search-result-${r.page}-${i}`}
                          >
                            <div className={`flex-shrink-0 w-11 h-11 rounded-lg flex flex-col items-center justify-center font-black ${isDark ? 'bg-red-500/20 text-red-300 group-hover:bg-red-500 group-hover:text-white' : 'bg-red-500/10 text-red-600 group-hover:bg-red-500 group-hover:text-white'} transition`}>
                              <span className="text-[9px] uppercase tracking-widest opacity-70">P.</span>
                              <span className="text-[13px] leading-none">{r.page}</span>
                            </div>
                            <div className={`flex-1 min-w-0 text-[12px] leading-relaxed ${textPrimary}`}>
                              <span className={textSecondary}>{r.prefix}{before}</span>
                              <mark className="bg-yellow-400 text-black px-0.5 rounded-sm font-black">{match}</mark>
                              <span className={textSecondary}>{after}{r.suffix}</span>
                            </div>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </>
              )}
            </div>

            {/* Footer hint */}
            <div className={`px-4 py-2 border-t text-[9px] text-center ${textSecondary} ${isDark ? 'border-white/10' : 'border-black/10'}`}>
              <kbd className={`px-1 rounded ${isDark ? 'bg-white/10' : 'bg-black/10'}`}>Échap</kbd> fermer · Clique un résultat pour ouvrir la page
            </div>
          </div>
        </div>
      )}

      <style>{`
        .flipbook-shell { max-width: 100%; }
        .flipbook { touch-action: pan-y; }
        .page-wrapper {
          background: #fff;
          box-shadow: 0 10px 40px rgba(0,0,0,.35);
          border-radius: 3px;
          overflow: hidden;
          height: 100%;
          width: 100%;
          position: relative;
        }
        .page-wrapper.theme-light { box-shadow: 0 10px 40px rgba(0,0,0,.15); }
        .page-wrapper img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          display: block;
          user-select: none;
          -webkit-user-drag: none;
        }
        .page-wrapper .page-footer {
          position: absolute; bottom: 0; left: 0; right: 0;
          text-align: center; padding: 2px 0;
          font-family: 'Figtree', sans-serif;
          font-size: 8px; color: #aaa; letter-spacing: 1.5px;
          pointer-events: none; background: rgba(255,255,255,0.85);
        }
      `}</style>
    </div>
  );
}
